import { useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Sparkles, Brain, Mail, NotebookText } from "lucide-react";
import { toast } from "sonner";
import MarkdownOutput from "../components/MarkdownOutput";

export default function AIHub() {
    return (
        <div data-testid="ai-hub-page">
            <PageHeader title="AI Hub" subtitle="Claude Sonnet 4.6 · Dealmaker-grade assistance.">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-mono flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Powered by Claude
                </span>
            </PageHeader>

            <div className="p-6 lg:p-8 max-w-5xl">
                <Tabs defaultValue="insights">
                    <TabsList className="grid grid-cols-3 max-w-md mb-6" data-testid="ai-hub-tabs">
                        <TabsTrigger value="insights" data-testid="ai-tab-insights"><Brain className="w-3.5 h-3.5 mr-1.5" />Insights</TabsTrigger>
                        <TabsTrigger value="summariser" data-testid="ai-tab-summariser"><NotebookText className="w-3.5 h-3.5 mr-1.5" />Notes</TabsTrigger>
                        <TabsTrigger value="email" data-testid="ai-tab-email"><Mail className="w-3.5 h-3.5 mr-1.5" />Email</TabsTrigger>
                    </TabsList>

                    <TabsContent value="insights"><Insights /></TabsContent>
                    <TabsContent value="summariser"><Summariser /></TabsContent>
                    <TabsContent value="email"><EmailDraft /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function Insights() {
    const [out, setOut] = useState("");
    const [loading, setLoading] = useState(false);
    const run = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/ai/deal-insights", {});
            setOut(data.insights);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="border border-border rounded-md bg-white p-6">
            <h3 className="font-display text-lg font-bold tracking-tight mb-2">Pipeline insights</h3>
            <p className="text-sm text-muted-foreground mb-4">AI reviews your entire pipeline and identifies risks, high-probability wins, and stale deals.</p>
            <Button onClick={run} disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="run-insights-btn">
                <Brain className="w-4 h-4 mr-2" />{loading ? "Analysing…" : "Analyse pipeline"}
            </Button>
            {out && <MarkdownOutput testid="insights-output">{out}</MarkdownOutput>}
        </div>
    );
}

function Summariser() {
    const [notes, setNotes] = useState("");
    const [out, setOut] = useState("");
    const [loading, setLoading] = useState(false);
    const run = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/ai/summarise-notes", { notes });
            setOut(data.summary);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="border border-border rounded-md bg-white p-6">
            <h3 className="font-display text-lg font-bold tracking-tight mb-2">Note summariser</h3>
            <p className="text-sm text-muted-foreground mb-4">Paste meeting notes and get a clean structured summary with key points, risks, and next steps.</p>
            <Textarea rows={8} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Paste messy meeting notes here…" data-testid="hub-summariser-input" />
            <Button onClick={run} disabled={loading || !notes.trim()} className="mt-3 bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="hub-summarise-btn">
                {loading ? "Summarising…" : "Summarise"}
            </Button>
            {out && <MarkdownOutput testid="hub-summariser-output">{out}</MarkdownOutput>}
        </div>
    );
}

function EmailDraft() {
    const [type, setType] = useState("teaser");
    const [recipient, setRecipient] = useState("");
    const [context, setContext] = useState("");
    const [out, setOut] = useState("");
    const [loading, setLoading] = useState(false);
    const run = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/ai/draft-email", {
                email_type: type,
                recipient_name: recipient || null,
                extra_context: context || null,
            });
            setOut(data.email);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="border border-border rounded-md bg-white p-6">
            <h3 className="font-display text-lg font-bold tracking-tight mb-2">Email drafter</h3>
            <p className="text-sm text-muted-foreground mb-4">Generate professional M&A emails — teaser, follow-up, outreach, NDA request, IOI chase, or CIM introduction.</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
                <Select value={type} onValueChange={setType}>
                    <SelectTrigger data-testid="hub-email-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="teaser">Teaser</SelectItem>
                        <SelectItem value="follow_up">Follow-up</SelectItem>
                        <SelectItem value="outreach">Buyer outreach</SelectItem>
                        <SelectItem value="nda_request">NDA Request</SelectItem>
                        <SelectItem value="ioi_chase">IOI Chase</SelectItem>
                        <SelectItem value="cim_intro">CIM Introduction</SelectItem>
                    </SelectContent>
                </Select>
                <Input placeholder="Recipient name (optional)" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
            </div>
            <Textarea rows={4} value={context} onChange={(e) => setContext(e.target.value)} placeholder="Extra context (sector, deal size, location, etc.)" data-testid="hub-email-context" />
            <Button onClick={run} disabled={loading} className="mt-3 bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="hub-draft-email-btn">
                {loading ? "Drafting…" : "Draft email"}
            </Button>
            {out && <MarkdownOutput mono testid="hub-email-output">{out}</MarkdownOutput>}
        </div>
    );
}
